import { useEffect, useMemo, useRef, useState } from "react"
import { DateRangePicker } from "../components/date-range-picker"
import { format } from "date-fns"
import { useNavigate } from "react-router-dom"
import { useAppSelector, useAppDispatch } from "../app/hooks"
import {
    fetchJobs,
    selectJobsError,
    selectJobsList,
    selectJobsPagination,
    selectJobsStatus,
} from "../features/jobs/jobSlice"
import { ensureCurrentUser } from "../features/auth/authSlice"
import { fetchFiltersDepartments } from "../features/filters/filterSlice"
import type { JobListFilters } from "../features/jobs/jobAPI"
import { ProfileImage } from "../components/ProfileImage"
import StyledSelectDropdown from "../components/StyledSelectDropdown"
import DropdownChevron from "../components/dropdown-chevron"

/** Persist All Jobs list UI so returning from job detail / matches restores page and filters. */
const ALL_JOBS_LIST_STATE_KEY = "job_me_in_all_jobs_list_state_v1"

type PersistedAllJobsListState = {
    page: number
    activeTab: string
    selectedDepartment: string
    jobTitleSearch: string
    dateFromMs: number | null
    dateToMs: number | null
}

function readPersistedAllJobsListState(): PersistedAllJobsListState | null {
    try {
        const raw = sessionStorage.getItem(ALL_JOBS_LIST_STATE_KEY)
        if (!raw) return null
        const p = JSON.parse(raw) as Partial<PersistedAllJobsListState>
        if (typeof p.page !== "number" || p.page < 1) return null
        return {
            page: p.page,
            activeTab: typeof p.activeTab === "string" ? p.activeTab : "All jobs",
            selectedDepartment: typeof p.selectedDepartment === "string" ? p.selectedDepartment : "",
            jobTitleSearch: typeof p.jobTitleSearch === "string" ? p.jobTitleSearch : "",
            dateFromMs: typeof p.dateFromMs === "number" ? p.dateFromMs : null,
            dateToMs: typeof p.dateToMs === "number" ? p.dateToMs : null,
        }
    } catch {
        return null
    }
}

function persistAllJobsListState(state: PersistedAllJobsListState) {
    try {
        sessionStorage.setItem(ALL_JOBS_LIST_STATE_KEY, JSON.stringify(state))
    } catch {
        /* ignore quota / private mode */
    }
}

const AllJobs = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const user = useAppSelector((state) => state.auth.user);
    const accessToken = useAppSelector((state) => state.auth.accessToken);
    const jobs = useAppSelector(selectJobsList);
    const jobsStatus = useAppSelector(selectJobsStatus);
    const jobsError = useAppSelector(selectJobsError);
    const jobsPagination = useAppSelector(selectJobsPagination);

    const departments = useAppSelector((state) => state.filters.departments);
    const departmentsStatus = useAppSelector((state) => state.filters.departmentsStatus);

    const persistedList = readPersistedAllJobsListState()

    const [activeTab, setActiveTab] = useState(persistedList?.activeTab ?? "All jobs")
    const [selectedDepartment, setSelectedDepartment] = useState<string>(
        persistedList?.selectedDepartment ?? ""
    )
    const [currentPage, setCurrentPage] = useState(persistedList?.page ?? 1)
    const [jobTitleInput, setJobTitleInput] = useState(persistedList?.jobTitleSearch ?? "")
    const [jobTitleSearch, setJobTitleSearch] = useState(persistedList?.jobTitleSearch ?? "")
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>(() => {
        if (!persistedList) return { from: undefined, to: undefined }
        const from =
            persistedList.dateFromMs != null ? new Date(persistedList.dateFromMs) : undefined
        const to = persistedList.dateToMs != null ? new Date(persistedList.dateToMs) : undefined
        return {
            from: from && Number.isFinite(from.getTime()) ? from : undefined,
            to: to && Number.isFinite(to.getTime()) ? to : undefined,
        }
    })
    const [userGroups, setUserGroups] = useState<string[]>([])
    const [isJobTitleSearchLoading, setIsJobTitleSearchLoading] = useState(false)
    const previousFilterKeyRef = useRef("")
    const jobTitleSearchFetchStartedRef = useRef(false)
    const isJobTitleSearchLoadingRef = useRef(false)

    // Fetch user groups from API to ensure we have the latest data
    useEffect(() => {
        const loadUserGroups = async () => {
            if (!accessToken) return

            try {
                const ensuredUser = await dispatch(ensureCurrentUser()).unwrap()
                const groups = ensuredUser.groups ?? []
                setUserGroups(Array.isArray(groups) ? (groups as string[]) : [])
            } catch (error) {
                console.error("Failed to fetch current user:", error)
                // Fallback to Redux state if API call fails
                const groups = Array.isArray(user?.groups) ? (user!.groups as string[]) : []
                setUserGroups(groups)
            }
        }

        loadUserGroups()
    }, [accessToken, user, user?.groups, dispatch])

    const isHiringManager = userGroups.includes("company_hiring_manager")

    const ITEMS_PER_PAGE = 7
    const MAX_VISIBLE_PAGES = 9

    const displayedJobs = jobs
    const totalPages = jobsPagination?.totalPages || 0
    const showTableLoadingState = jobsStatus === "loading" && displayedJobs.length === 0
    const filterKey = useMemo(
        () =>
            JSON.stringify({
                activeTab,
                selectedDepartment,
                jobTitleSearch,
                time_range_start: dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "",
                time_range_end: dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "",
            }),
        [activeTab, selectedDepartment, jobTitleSearch, dateRange]
    )

    useEffect(() => {
        persistAllJobsListState({
            page: currentPage,
            activeTab,
            selectedDepartment,
            jobTitleSearch,
            dateFromMs: dateRange.from?.getTime() ?? null,
            dateToMs: dateRange.to?.getTime() ?? null,
        })
    }, [currentPage, activeTab, selectedDepartment, jobTitleSearch, dateRange.from, dateRange.to])

    // Calculate sliding window for pagination
    // Window slides forward when clicking next at the end, slides backward when clicking prev at the start
    const getPaginationWindow = () => {
        if (totalPages === 0) {
            return { startPage: 1, endPage: 0 }
        }
        if (totalPages <= MAX_VISIBLE_PAGES) {
            // If total pages is less than or equal to max visible, show all pages
            return { startPage: 1, endPage: totalPages }
        }

        let startPage: number

        // If we're at page 1, always show window 1-9
        if (currentPage === 1) {
            startPage = 1
        }
        // If we're at the last page, show the last 9 pages
        else if (currentPage === totalPages) {
            startPage = totalPages - MAX_VISIBLE_PAGES + 1
        }
        // For pages 2-9, show window 1-9
        else if (currentPage <= MAX_VISIBLE_PAGES) {
            startPage = 1
        }
        // For pages near the end (within last 9 pages), show the last 9 pages
        else if (currentPage > totalPages - MAX_VISIBLE_PAGES) {
            startPage = totalPages - MAX_VISIBLE_PAGES + 1
        }
        else {
            // Position window so currentPage is at the last position (9th) in the 9-page window
            startPage = currentPage - MAX_VISIBLE_PAGES + 1
        }

        const endPage = Math.min(startPage + MAX_VISIBLE_PAGES - 1, totalPages)
        return { startPage, endPage }
    }

    const { startPage, endPage } = getPaginationWindow()
    const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [totalPages, currentPage])

    // Fetch departments on mount
    useEffect(() => {
        dispatch(fetchFiltersDepartments());
    }, [dispatch]);

    // Fetch jobs when filters or page change using API limit/offset pagination.
    useEffect(() => {
        if (!accessToken) return;

        const loadJobs = async () => {
            try {
                if (
                    previousFilterKeyRef.current &&
                    previousFilterKeyRef.current !== filterKey &&
                    currentPage !== 1
                ) {
                    previousFilterKeyRef.current = filterKey
                    setCurrentPage(1)
                    return
                }

                previousFilterKeyRef.current = filterKey
                let companyMemberId = user?.company_member_id;

                if (!companyMemberId) {
                    const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
                    companyMemberId = ensuredUser.company_member_id as number | undefined;
                }

                if (companyMemberId) {
                    const filters: JobListFilters = {
                        sort_by: "-created_at",
                        limit: ITEMS_PER_PAGE,
                        offset: (currentPage - 1) * ITEMS_PER_PAGE,
                    };

                    // Add status filter (map "All jobs" to undefined, "Closed" to "Close")
                    if (activeTab !== "All jobs") {
                        filters.status = activeTab === "Closed" ? "Close" : activeTab;
                    }

                    if (jobTitleSearch.trim()) {
                        filters.job_title = jobTitleSearch.trim();
                    }

                    // Add department filter
                    if (selectedDepartment) {
                        const selectedDept = departments.find(dept => dept.name === selectedDepartment);
                        if (selectedDept) {
                            filters.department_ids = [selectedDept.id];
                        }
                    }

                    // Add date range filters (format as YYYY-MM-DD)
                    if (dateRange.from && dateRange.to) {
                        filters.time_range_start = format(dateRange.from, "yyyy-MM-dd");
                        filters.time_range_end = format(dateRange.to, "yyyy-MM-dd");
                    }

                    dispatch(fetchJobs({ accessToken, filters }));
                } else if (isJobTitleSearchLoadingRef.current) {
                    isJobTitleSearchLoadingRef.current = false
                    setIsJobTitleSearchLoading(false)
                    jobTitleSearchFetchStartedRef.current = false
                }
            } catch (error) {
                console.error("Failed to fetch jobs:", error);
                if (isJobTitleSearchLoadingRef.current) {
                    isJobTitleSearchLoadingRef.current = false
                    setIsJobTitleSearchLoading(false)
                    jobTitleSearchFetchStartedRef.current = false
                }
            }
        };

        loadJobs();
    }, [
        dispatch,
        user?.company_member_id,
        accessToken,
        currentPage,
        filterKey,
        departments,
        activeTab,
        selectedDepartment,
        dateRange.from,
        dateRange.to,
        jobTitleSearch,
    ]);

    useEffect(() => {
        if (!isJobTitleSearchLoadingRef.current) return

        if (jobsStatus === "loading") {
            jobTitleSearchFetchStartedRef.current = true
            return
        }

        if (jobTitleSearchFetchStartedRef.current && (jobsStatus === "succeeded" || jobsStatus === "failed")) {
            isJobTitleSearchLoadingRef.current = false
            setIsJobTitleSearchLoading(false)
            jobTitleSearchFetchStartedRef.current = false
        }
    }, [jobsStatus])

    const handleJobTitleSearch = () => {
        const nextSearch = jobTitleInput.trim()
        const willFetch = nextSearch !== jobTitleSearch || currentPage !== 1

        if (willFetch) {
            jobTitleSearchFetchStartedRef.current = false
            isJobTitleSearchLoadingRef.current = true
            setIsJobTitleSearchLoading(true)
        }

        setJobTitleSearch(nextSearch)
        if (currentPage !== 1) {
            setCurrentPage(1)
        }
    }

    const handleRowClick = (jobId: number) => {
        navigate(`/jobs/${jobId}`)
    }

    return (
        <div className="min-h-screen p-5">
            <div className="mx-auto max-w-7xl">
                {/* Header Section */}
                <div className="mb-6 flex items-start justify-between">
                    {/* Department Filter */}
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="flex flex-col gap-2">
                                <label htmlFor="department" className="text-base font-bold text-black">
                                    Department
                                </label>
                                <StyledSelectDropdown
                                    options={[
                                        { id: "", name: "All" },
                                        ...(departmentsStatus === "succeeded" ? departments : []),
                                    ]}
                                    value={selectedDepartment}
                                    onChange={setSelectedDepartment}
                                    placeholder="All"
                                    disabled={departmentsStatus === "loading"}
                                    className="w-72"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label htmlFor="jobTitleSearch" className="text-base font-bold text-black">
                                    Job title
                                </label>
                                <input
                                    id="jobTitleSearch"
                                    type="text"
                                    value={jobTitleInput}
                                    onChange={(e) => setJobTitleInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault()
                                            handleJobTitleSearch()
                                        }
                                    }}
                                    placeholder="Search by job title"
                                    className="w-72 rounded-md bg-[#F9F9F9] px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                                />
                            </div>
                        </div>

                        {/* Tab Filters */}
                        <div className="mt-2 flex gap-2">
                            {["All jobs", "Open", "On-hold", "Closed"].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`rounded-md px-6 py-2 text-sm font-medium transition-colors ${activeTab === tab ? "bg-[#000000] text-[#FFFFFF]" : "bg-white border border-[#ADA7A7] text-black hover:bg-gray-200"
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Right Side - Create Button and Time Range */}
                    <div className="flex flex-col items-end gap-11">
                        {!isHiringManager && (
                            <button
                                onClick={() => { navigate('/create-job') }}
                                className="btn-cta flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                Create a job
                            </button>
                        )}

                        <div className="flex flex-col items-start gap-2">
                            <label htmlFor="timeRange" className="text-sm font-bold text-black">
                                Date range
                            </label>
                            <button
                                id="timeRange"
                                onClick={() => setIsPickerOpen(true)}
                                className="flex w-56 font-medium items-center justify-between rounded-md bg-[#F1F1F1] px-4 py-2 text-sm text-black hover:bg-[#e5e5e5] transition-colors"
                            >
                                <span>
                                    {dateRange.from && dateRange.to
                                        ? `${format(dateRange.from, "MM/dd/yyyy")} - ${format(dateRange.to, "MM/dd/yyyy")}`
                                        : "Select date range"
                                    }
                                </span>
                                <DropdownChevron isOpen={isPickerOpen} />
                            </button>
                        </div>
                    </div>
                </div>

                {isPickerOpen && (
                    <DateRangePicker
                        isOpen={isPickerOpen}
                        onClose={() => setIsPickerOpen(false)}
                        onUpdate={(range) => {
                            setDateRange({ from: range.from, to: range.to });
                            setIsPickerOpen(false)
                        }}
                        initialRange={dateRange}
                    />
                )}

                {/* Table */}
                <div className="overflow-hidden rounded-md">
                    <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-[980px] table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[22%]" />
                                <col className="w-[12%]" />
                                <col className="w-[16%]" />
                                <col className="w-[20%]" />
                                <col className="w-[10%]" />
                                <col className="w-[10%]" />
                                <col className="w-[10%]" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-left text-sm text-black bg-[#CDD6DF] rounded-l-md">Position</th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-left text-sm text-black bg-[#CDD6DF]">
                                        <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                                            Days open
                                            <span className="mt-1 shrink-0">
                                                <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M0.739692 5.36845H9.26102C9.89715 5.36845 10.2351 4.72395 9.81229 4.31639L5.55199 0.212426C5.48289 0.14564 5.39801 0.092172 5.30299 0.0555535C5.20796 0.0189349 5.10494 0 5.00073 0C4.89652 0 4.7935 0.0189349 4.69847 0.0555535C4.60344 0.092172 4.51857 0.14564 4.44946 0.212426L0.187691 4.31639C-0.235165 4.72395 0.102825 5.36845 0.739692 5.36845ZM4.44873 11.7876C4.51783 11.8544 4.6027 11.9078 4.69773 11.9444C4.79276 11.9811 4.89578 12 4.99999 12C5.1042 12 5.20722 11.9811 5.30225 11.9444C5.39728 11.9078 5.48215 11.8544 5.55125 11.7876L9.81155 7.68361C10.2351 7.27669 9.89715 6.63218 9.26029 6.63218H0.739692C0.103563 6.63218 -0.234427 7.27669 0.188429 7.68424L4.44873 11.7876Z" fill="#9E9E9E" />
                                                </svg>
                                            </span>
                                        </div>
                                    </th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Department</th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Hiring manager</th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">In match</th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Top match</th>
                                    <th className="whitespace-nowrap px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF] rounded-r-md">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {isJobTitleSearchLoading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12">
                                            <div className="flex justify-center">
                                                <div className="h-8 w-8 rounded-md border-2 border-gray-400 border-t-transparent animate-spin" />
                                            </div>
                                        </td>
                                    </tr>
                                ) : showTableLoadingState ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                                            Loading jobs...
                                        </td>
                                    </tr>
                                ) : jobsError ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-red-500">
                                            {jobsError}
                                        </td>
                                    </tr>
                                ) : displayedJobs.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                                            No jobs found.
                                        </td>
                                    </tr>
                                ) : (
                                    displayedJobs.map((job) => {
                                        const daysOpenDisplay =
                                            job.daysOpen != null ? job.daysOpen + 1 : 0
                                        const isLongOpen = daysOpenDisplay >= 30

                                        return (
                                        <tr
                                            key={job.id}
                                            onClick={() => handleRowClick(job.id)}
                                            className="transition-colors hover:bg-gray-100"
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex min-w-0 flex-col">
                                                    <span className="truncate text-xs font-normal text-[#192A47]">{job.position || "N/A"}</span>
                                                    <span className="truncate text-xs font-normal text-[#192A47]">{job.location}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-[#192A47]">
                                                <span
                                                    className={`block truncate ${isLongOpen ? "font-bold text-[#ABD405]" : ""}`}
                                                >
                                                    {daysOpenDisplay}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-normal text-[#192A47]">
                                                <span className="block truncate">{job.department}</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex min-w-0 items-center justify-center gap-3">
                                                    <ProfileImage
                                                        profilePicture={job.hiringManager.avatar}
                                                        profilePictureUrl={job.hiringManager.avatarUrl}
                                                        accessToken={accessToken || undefined}
                                                        alt={job.hiringManager.name}
                                                        className="min-h-10 min-w-10 max-h-10 max-w-10 shrink-0 rounded-md bg-gray-200 border-2 border-black"
                                                        fallbackPath="/assets/images/Avatar2.jpg"
                                                    />
                                                    <span className="truncate text-sm font-normal text-[#192A47]">
                                                        {job.hiringManager.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                                                <span className="block truncate">{job.inMatch}</span>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                                                <span className="block truncate">{job.topMatch}%</span>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-normal text-[#192A47]">
                                                <span className="block truncate">{job.status}</span>
                                            </td>
                                        </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination - Client-side pagination with sliding window */}
                {totalPages > 0 && (
                    <div className="mt-6 flex items-center justify-end gap-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1 || jobsStatus === "loading"}
                            className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Previous
                        </button>

                        {/* Show page numbers with sliding window */}
                        {visiblePages.map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                disabled={jobsStatus === "loading"}
                                className={`flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold transition-colors ${currentPage === page ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-100"
                                    } disabled:opacity-50`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages || jobsStatus === "loading"}
                            className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AllJobs
